import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../contexts/CurrencyContext';
import { cn } from '../../utils/cn';
import LoadingSpinner from '../ui/LoadingSpinner';

interface CurrencyDisplayProps {
    amount: number;
    currency: string;
    className?: string;
    showOriginal?: boolean;
    compact?: boolean;
    convertTo?: string;
    showConversionInfo?: boolean;
    showLoadingState?: boolean;
    onClick?: () => void;

    // Advanced display options
    showCurrencyCode?: boolean;
    showCurrencyFlag?: boolean;
    alignment?: 'left' | 'center' | 'right';
    size?: 'sm' | 'md' | 'lg' | 'xl';
    variant?: 'default' | 'muted' | 'success' | 'warning' | 'error';

    // Tooltip options
    showTooltip?: boolean;
    tooltipContent?: string;
}

const CurrencyDisplay: React.FC<CurrencyDisplayProps> = ({
    amount,
    currency,
    className = '',
    showOriginal = false,
    compact = false,
    convertTo,
    showConversionInfo = false,
    showLoadingState = true,
    onClick,
    showCurrencyCode = false,
    showCurrencyFlag = false,
    alignment = 'left',
    size = 'md',
    variant = 'default',
    showTooltip = false,
    tooltipContent,
}) => {
    const {
        userCurrency,
        convertAmount,
        formatCurrency,
        formatCurrencyCompact,
        getCachedRate,
        getCurrencyInfo,
        isRateAvailable,
    } = useCurrency();

    const [convertedAmount, setConvertedAmount] = useState<number | null>(null);
    const [conversionRate, setConversionRate] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [conversionError, setConversionError] = useState<string | null>(null);

    const targetCurrency = convertTo || userCurrency;
    const needsConversion = currency !== targetCurrency;
    const formatter = compact ? formatCurrencyCompact : formatCurrency;

    // Size mappings
    const sizeClasses = {
        sm: 'text-sm',
        md: 'text-base',
        lg: 'text-lg font-medium',
        xl: 'text-xl font-semibold',
    };

    // Variant mappings
    const variantClasses = {
        default: 'text-gray-900 dark:text-white',
        muted: 'text-gray-600 dark:text-gray-400',
        success: 'text-green-600 dark:text-green-400',
        warning: 'text-amber-600 dark:text-amber-400',
        error: 'text-red-600 dark:text-red-400',
    };

    // Alignment mappings
    const alignmentClasses = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right',
    };

    useEffect(() => {
        if (!needsConversion) {
            setConvertedAmount(amount);
            setConversionRate(1);
            return;
        }

        const performConversion = async () => {
            try {
                setIsLoading(true);
                setConversionError(null);

                // Check if rate is available in cache for instant display
                const cachedRate = getCachedRate(currency, targetCurrency);
                if (cachedRate !== null) {
                    setConvertedAmount(amount * cachedRate);
                    setConversionRate(cachedRate);
                }

                // Always fetch fresh data in background
                const result = await convertAmount(amount, currency, targetCurrency);
                setConvertedAmount(result.convertedAmount);
                setConversionRate(result.rate);
            } catch (error) {
                console.error('Currency conversion failed:', error);
                setConversionError('Conversion failed');

                // Fallback to original amount
                setConvertedAmount(amount);
                setConversionRate(1);
            } finally {
                setIsLoading(false);
            }
        };

        performConversion();
    }, [amount, currency, targetCurrency, convertAmount, getCachedRate, needsConversion]);

    // Get currency info for flags/symbols
    const originalCurrencyInfo = getCurrencyInfo(currency);
    const targetCurrencyInfo = getCurrencyInfo(targetCurrency);

    const renderOriginalAmount = () => {
        if (!showOriginal || !needsConversion) return null;

        return (
            <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">
                ({formatter(amount, currency)})
            </span>
        );
    };

    const renderConversionInfo = () => {
        if (!showConversionInfo || !needsConversion || !conversionRate) return null;

        return (
            <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                1 {currency} = {conversionRate.toFixed(4)} {targetCurrency}
            </div>
        );
    };

    const renderCurrencyCode = () => {
        if (!showCurrencyCode) return null;

        return (
            <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">
                {needsConversion ? targetCurrency : currency}
            </span>
        );
    };

    const renderCurrencyFlag = () => {
        if (!showCurrencyFlag) return null;

        const currencyInfo = needsConversion ? targetCurrencyInfo : originalCurrencyInfo;
        if (!currencyInfo?.flag) return null;

        return (
            <img
                src={currencyInfo.flag}
                alt={currencyInfo.code}
                className="w-4 h-4 ml-1 inline-block"
            />
        );
    };

    const renderLoadingState = () => {
        if (!showLoadingState || !isLoading || !needsConversion) return null;

        return (
            <div className="inline-flex items-center ml-2">
                <LoadingSpinner size="xs" />
            </div>
        );
    };

    const renderConversionError = () => {
        if (!conversionError) return null;

        return (
            <span className="text-red-500 dark:text-red-400 text-sm ml-2" title={conversionError}>
                *
            </span>
        );
    };

    const displayAmount = convertedAmount ?? amount;
    const displayCurrency = needsConversion ? targetCurrency : currency;

    const baseClasses = cn(
        sizeClasses[size],
        variantClasses[variant],
        alignmentClasses[alignment],
        onClick && 'cursor-pointer hover:opacity-80',
        className
    );

    const content = (
        <div className={baseClasses} onClick={onClick}>
            <span className="tabular-nums">
                {formatter(displayAmount, displayCurrency)}
            </span>

            {renderCurrencyCode()}
            {renderCurrencyFlag()}
            {renderOriginalAmount()}
            {renderLoadingState()}
            {renderConversionError()}

            {renderConversionInfo()}
        </div>
    );

    if (showTooltip && (tooltipContent || needsConversion)) {
        const defaultTooltip = needsConversion
            ? `Original: ${formatter(amount, currency)}\nRate: 1 ${currency} = ${conversionRate?.toFixed(4) || '...'} ${targetCurrency}`
            : `${formatter(amount, currency)}`;

        return (
            <div title={tooltipContent || defaultTooltip} className="inline-block">
                {content}
            </div>
        );
    }

    return content;
};

export default CurrencyDisplay;